import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FreshnessChip } from '@/components/tracking/FreshnessChip';
import { useState } from 'react';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { TrendChart } from '@/components/tracking/TrendChart';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { projectPnl, latestMetricValue } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import type { AccountRole } from '@/types/tracking-view';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/12-month-pl — the 12-Month P&L tool (F-013 S6.2). A forward P&L from the current
 * run-rate (straight-line, no seasonality — stated on the page). Real + flag-gated.
 */
export const Route = createFileRoute('/finance/forecast_/12-month-pl')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <TwelveMonthPnl />
    </FinanceGuard>
  ),
});

function TwelveMonthPnl() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const cur = periods[periods.length - 1]?.roleTotals ?? {};
  const n = (k: AccountRole) => cur[k] ?? 0;

  const margin = latestMetricValue(model?.metricsById['gross-margin']) ?? 25;
  const [growth, setGrowth] = useState(0);
  const p = projectPnl({ revenue: n('revenue'), marginPct: margin, overhead: n('overhead') }, { growthPct: growth, months: 12 });
  const netHistory = p.months.map((m) => ({ period: `M${m.month}`, value: m.netProfit }));

  const Tot = ({ l, v, pos }: { l: string; v: number; pos?: boolean }) => (
    <div className="rounded-xl border border-border bg-card p-3">
      <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">{l}</span>
      <span className={`ds-kpi-md ${pos ? (v >= 0 ? 'text-success' : 'text-danger') : 'text-foreground'}`}>{fmtCurrency(v)}</span>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2"><h1 className="ds-h1">12-Month P&amp;L</h1><FreshnessChip fetchedAt={model?.fetchedAt} /></div>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">Your profit and loss projected a year forward, at your current run-rate.</p>

      {!model || periods.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync to project your annual P&amp;L.</p>
        </div>
      ) : (
        <>
          <div className="mb-3.5 rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Monthly net profit · next 12 months</p>
              <div className="w-full max-w-[220px]">
                <FinanceSlider label="Revenue growth / mo" value={growth} set={setGrowth} min={-5} max={12} step={0.5} fmt={(v) => `${v > 0 ? '+' : ''}${v}%`} />
              </div>
            </div>
            <TrendChart history={netHistory} unit="currency" benchmark={0} benchmarkLabel="break-even" height={150} showYAxis />
          </div>
          <div className="mb-3.5 grid grid-cols-2 gap-2.5 md:grid-cols-4">
            <Tot l="Revenue (12 mo)" v={p.totals.revenue} />
            <Tot l="Gross profit" v={p.totals.grossProfit} />
            <Tot l="Overheads" v={p.totals.overhead} />
            <Tot l="Net profit" v={p.totals.netProfit} pos />
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-[12px]">
              <thead><tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-bold">Month</th><th className="px-3 py-2 text-right font-bold">Revenue</th><th className="px-3 py-2 text-right font-bold">Gross profit</th><th className="px-3 py-2 text-right font-bold">Net profit</th>
              </tr></thead>
              <tbody>
                {p.months.map((m) => (
                  <tr key={m.month} className="border-b border-border/60 last:border-b-0">
                    <td className="px-3 py-1.5 text-muted-foreground">Month {m.month}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtCurrency(m.revenue)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtCurrency(m.grossProfit)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${m.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtCurrency(m.netProfit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground">Straight-line at your current margin and overheads — seasonality and one-offs aren’t modelled yet. Use the Forecast levers to model growth.</p>
        </>
      )}
      <ForecastToolNav current="12-month-pl" />
    </div>
  );
}
